import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { http } from '../api/http';
import { useAuthStore } from '../store/auth.store';

/** État de la fiche de l'école pour l'émission des factures (F46, suite). */
export interface LegalProfileStatus {
  missing: string[];
  /** Date (AAAA-MM-JJ, jour de Paris) à partir de laquelle l'émission est bloquée ; absente : avertissement seul. */
  enforcedFrom: string | null;
  enforced: boolean;
}

const frDate = (day: string) => day.split('-').reverse().join('/');
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** Phrase du bandeau : ce qui manque et, s'il y en a une, la date du blocage. */
export function legalProfileMessage(status: LegalProfileStatus): string {
  const missing = capitalize(status.missing.join(' ; '));
  if (!status.enforcedFrom) return `Fiche de l’auto-école incomplète : ${missing}. Ces mentions sont obligatoires sur les factures.`;
  return status.enforced
    ? `Fiche de l’auto-école incomplète : ${missing}. Depuis le ${frDate(status.enforcedFrom)}, l’émission des factures et les commandes en ligne sont bloquées.`
    : `Fiche de l’auto-école incomplète : ${missing}. À partir du ${frDate(status.enforcedFrom)}, l’émission des factures et les commandes en ligne seront bloquées.`;
}

/**
 * Bandeau du gérant et du responsable tant que la fiche de l'école n'a pas de SIRET valide ou
 * d'adresse du siège : ce qui manque, la date du blocage, lien vers Réglages › Fiche de l'auto-école.
 */
export function LegalProfileBanner() {
  const role = useAuthStore((s) => s.user?.role ?? '');
  const schoolId = useAuthStore((s) => s.activeSchoolId);
  const allowed = role === 'owner' || role === 'manager';
  const { data } = useQuery({
    queryKey: ['legal-profile-status', schoolId],
    queryFn: async () => (await http.get<LegalProfileStatus>('/v1/invoices/legal-profile-status')).data,
    enabled: allowed && !!schoolId,
    staleTime: 10 * 60 * 1000,
  });
  if (!allowed || !data?.missing?.length) return null;
  return (
    <div
      role="status"
      aria-label="Fiche de l’auto-école incomplète"
      style={{
        background: data.enforced ? '#FEE2E2' : '#FEF3C7',
        borderBottom: `1px solid ${data.enforced ? '#DC2626' : '#F59E0B'}`,
        padding: '0.6rem 1.5rem',
        fontSize: '0.875rem',
        color: data.enforced ? '#991B1B' : '#92400E',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <span>{legalProfileMessage(data)}</span>
      <Link to="/reglages/ecole" style={{ fontWeight: 600, textDecoration: 'underline' }}>
        Compléter la fiche de l’auto-école
      </Link>
    </div>
  );
}
